import React, {SFC, useEffect, useState} from "react";
import {
    Box,
    Button,
    Card,
    CircularProgress,
    FormControlLabel,
    Grid,
    PropTypes as MuIPropTypes,
    Radio,
    RadioGroup,
    TextField as MUITextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {TextField} from "formik-material-ui";
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from "yup";
import {useHistory} from "react-router-dom";
import {useSnackbar} from "notistack";
import {foundUsOptions} from "../../Constants/foundUsOptions";
import MenuItem from "@material-ui/core/MenuItem";
import moment from "moment-timezone";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {
    addAddress,
    createUserProfile,
    getUserProfile,
    patchUserProfile
} from "../../redux/userProfile/userProfile.actions";
import Address from "./Address";
import {IParentProfile} from "../../interfaces";
import {postReferralCode} from "../../redux/user/user.actions";
import {DatePicker} from "@material-ui/pickers";
import {colorError} from "../../Constants/theme";
import {states} from "../../Constants/locations";
import {zipCodes} from "../../Constants/zipcodes";

const useStyle = makeStyles(() => ({
    wrapper: {
        padding: "1.5em"
    },
    formItem: {
        marginBottom: "1em",
    },
    inputGroup: {
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center"
    },
    applyBtn: {
        height: 45
    },
    input: {
        padding: "0.8rem 1rem"
    },
    formError: {
        color: colorError,
        fontSize: "12px",
        marginLeft: "14px",
    },
}));

interface Props {
    isOnBoarding: boolean,
    setIsOnBoarding: React.Dispatch<React.SetStateAction<boolean>>,
}

const Basic: SFC<Props> = ({isOnBoarding, setIsOnBoarding}: Props) => {
    const classes = useStyle();
    const theme = useTheme();
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const textAlignmentAdaptive: MuIPropTypes.Alignment = isMobile ? "center" : "left";
    let initialValues: IParentProfile = {
        firstName: "",
        lastName: "",
        findUs: "",
        phone: "",
        birthday: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        country: "",
    };
    const phoneRegex = new RegExp("^(\\+\\d{1,2}\\s?)?\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}$");
    const [profileId, setProfileId] = useState(0);
    const [referralCode, setReferralCode] = useState("");
    const [applyButtonDisabled, setApplyButtonDisabled] = useState(true);
    const [referralCodeApplied, setReferralCodeApplied] = useState(false);
    const [formikValues, setFormikValues] = useState(initialValues);
    const [isProfileExists, setIsProfileExists] = useState(true);
    const [isLoaderShowed, setIsLoaderShowed] = useState(false);
    const [initialDataFetched, setInitialDataFetched] = useState(false);
    const [isSubmit, setSubmit] = useState(false);
    const initialAccessState = {
        location: true,
        camera: true,
        microphone: true,
    };
    const [accessData, setAccessData] = useState(initialAccessState);
    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required("Required"),
        lastName: Yup.string().required("Required"),
        state: Yup.string().required("Required"),
        findUs: Yup.string().required("Required"),
        birthday: Yup.string().required("Required"),
        phone: Yup.string().required("Required").matches(phoneRegex, "phone must match the following: (xxx)xxx-xxxx"),
        zip: Yup.string().length(5, "Please input a valid 5 digit zipcode").test("match", "Please input a valid 5 digit zipcode", val => {return zipCodes.includes(val);}).required("Required"),
    });
    const validationSchemaLabel = Yup.object().shape({
        firstName: Yup.string().required("Required"),
        lastName: Yup.string().required("Required"),
        state: Yup.string().required("Required"),
        birthday: Yup.string().required("Required"),
        phone: Yup.string().required("Required").matches(phoneRegex, "phone must match the following: (xxx)xxx-xxxx"),
        zip: Yup.string().length(5, "Please input a valid 5 digit zipcode").test("match", "Please input a valid 5 digit zipcode", val => {return zipCodes.includes(val);}).required("Required"),
    });
    const user = useSelector((state: RootState) => state?.user);
    const userProfile = useSelector((state: RootState) => state?.userProfile);
    const dispatch = useAppDispatch();
    const branding = useSelector((state: RootState) => state?.branding);
    const capitalize = s => (s && s[0].toUpperCase() + s.slice(1)) || "";
    const brandingName = isWhiteLabel ? capitalize(branding?.name) : "The Video Vets";
    const applyReferralCode = async (code) => {
        try {
            const referralCodeRequest = await dispatch(postReferralCode(code));
            setReferralCodeApplied(true);
            enqueueSnackbar(`$${referralCodeRequest.discountAmount.toFixed(2)} applied to your account`, {
                autoHideDuration: 4000
            });
        } catch (err) {
            console.log(err);
            enqueueSnackbar("Invalid referral code", {
                variant: "error",
                autoHideDuration: 4000
            });
        }
    };

    useEffect(() => {
        dispatch(getUserProfile({userId: user.userId})).then((profile) => {
            if (profile.length) {
                setIsOnBoarding(false);
                setIsProfileExists(true);
            }
        });
    }, []);

    useEffect(() => {
        const accessData = localStorage.getItem("accessData");
        if (accessData === null) {
            localStorage.setItem("accessData", JSON.stringify(initialAccessState));
        } else {
            setAccessData(JSON.parse(localStorage.getItem("accessData")));
        }
    }, []);

    useEffect(() => {
        if (referralCode.length) {
            setApplyButtonDisabled(false);
        } else {
            setApplyButtonDisabled(true);
        }
    }, [referralCode]);

    useEffect(() => {
        setIsLoaderShowed(true);
        if (isOnBoarding) {
            setIsProfileExists(false);
            const newFormikValues = {...formikValues};
            newFormikValues.firstName = userProfile.firstName || "";
            newFormikValues.lastName = userProfile.lastName || "";
            // @ts-ignore
            newFormikValues.birthday = userProfile.birthday || moment().format("YYYY-MM-DD");
            newFormikValues.state = states[0].value;
            setFormikValues({...newFormikValues});
            setIsLoaderShowed(false);
        } else {
            dispatch(getUserProfile({userId: user.userId})).then((profile) => {
                // Presetting fields with data from BE
                if (profile.length) {
                    setProfileId(profile[0].id);
                    const newFormikValues = {...formikValues};
                    newFormikValues.firstName = profile[0].firstName || "";
                    newFormikValues.lastName = profile[0].lastName || "";
                    newFormikValues.state = profile[0].usState || states[0].value;
                    newFormikValues.findUs = profile[0].findUs || "";
                    newFormikValues.phone = profile[0].phoneNumber || "";
                    newFormikValues.birthday = profile[0].birthday;
                    setFormikValues({...newFormikValues});
                } else {
                    // Changing that, so we could know that we are creating the new profile;
                    setIsProfileExists(false);
                }
            }).finally(() => {
                setInitialDataFetched(true);
                setIsLoaderShowed(false);
            });
        }
    }, [isOnBoarding]);

    const handleLocationAccess = (e, newValue) => {
        const {name} = e.target;
        const value = newValue === "true";
        setAccessData(prev => ({
            ...prev,
            [name] : value
        }));
    };

    return (
        <Formik
            initialValues={formikValues}
            enableReinitialize
            onSubmit={async (values: any) => {
                try {
                    const practiceCode = "01";
                    const stateOfResidence = values.state;
                    const userId = user.userId;
                    const phoneNumber = values.phone;
                    const type = "owner";
                    let body = {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        stateOfResidence: values.state,
                        practiceCode,
                        userId,
                        birthday: values.birthday,
                        type,
                        phoneNumber,
                        locationTimeZone: moment.tz.guess()
                    };
                    if (values.findUs) Object.assign(body, {findUs: values.findUs});
                    if (profileId !== 0) {
                        await dispatch(patchUserProfile({id: profileId, body: body}));
                    } else {
                        await dispatch(createUserProfile(body));
                        setIsProfileExists(true);
                    }
                    if (values.zip || values.city || values.address || values.state || values.country) {
                        if (values.zip && values.city && values.address && values.state && values.country) {
                            await dispatch(addAddress({
                                userId: user.userId,
                                zipCode: values.zip,
                                state: values.state,
                                city: values.city,
                                firstLine: values.address,
                                country: values.country,
                                defaultAddress: true,
                            }));
                        } else {
                            enqueueSnackbar("All fields for address should be inputted.", {
                                variant: "error",
                                autoHideDuration: 3000,
                            });
                            return;
                        }
                    }
                    localStorage.setItem("accessData", JSON.stringify(accessData));
                    if (isOnBoarding) {
                        enqueueSnackbar("Profile is saved. You can now add a pet", {
                            autoHideDuration: 2500
                        });
                        history.push("/pets/0/general");
                    } else {
                        enqueueSnackbar("Profile is saved.", {
                            autoHideDuration: 2500
                        });
                    }
                } catch (error) {
                    enqueueSnackbar(error.errorMessage, {
                        variant: "error",
                        autoHideDuration: 3000,
                    });
                }
            }}
            validationSchema={isWhiteLabel ? validationSchemaLabel : validationSchema}
        >
            {({values, handleChange, setFieldValue, handleBlur}) => {
                return (
                    <Form autoComplete="on">
                        <Card variant="outlined">
                            {/* Desiding if we want to show form or loader */}
                            {
                                !isLoaderShowed ?
                                    <Grid container className={classes.wrapper}>
                                        <Grid item container justify="space-between" alignItems="center">
                                            <Grid container alignItems="center" item xs={12}>
                                                <Box marginBottom="10px">
                                                    <Typography variant="h4">Your Profile</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item container alignItems="center" className={classes.formItem}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box marginBottom="10px">
                                                        <Typography
                                                            variant="h5"
                                                            align={textAlignmentAdaptive}
                                                        >
                                                            First Name
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Field
                                                        component={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        label="Your first name"
                                                        name="firstName"
                                                        id="firstName"
                                                        aria-labelledby="firstName"
                                                        type="text"
                                                        onChange={handleChange("firstName")}
                                                        onBlur={handleBlur("firstName")}
                                                        value={values.firstName}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid item container alignItems="center" className={classes.formItem}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box marginBottom="10px">
                                                        <Typography variant="h5" align={textAlignmentAdaptive}>Last
                                                            Name</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Field
                                                        component={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        label="Your last name"
                                                        name="lastName"
                                                        id="lastName"
                                                        aria-labelledby="lastName"
                                                        type="text"
                                                        onChange={handleChange("lastName")}
                                                        onBlur={handleBlur("lastName")}
                                                        value={values.lastName}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid container justify="space-between" alignItems="center"
                                            className={classes.formItem}>
                                            <Grid item xs={12} sm={6}>
                                                <Box marginBottom="10px">
                                                    <Typography variant="h5"
                                                        align={textAlignmentAdaptive}>Phone</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field
                                                    component={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    autoComplete="tel-national"
                                                    label="Your phone"
                                                    name="phone"
                                                    id="phone"
                                                    aria-labelledby="phone"
                                                    type="tel"
                                                    onChange={handleChange("phone")}
                                                    onBlur={handleBlur("phone")}
                                                    value={values.phone}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container justify="space-between" alignItems="center"
                                            className={classes.formItem}>
                                            <Grid item xs={12} sm={6}>
                                                <Box marginBottom="10px">
                                                    <Typography variant="h5"
                                                        align={textAlignmentAdaptive}>Birthday</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <DatePicker
                                                    name="birthday"
                                                    id="birthday"
                                                    aria-labelledby="birthday"
                                                    disableFuture={true}
                                                    error={isSubmit && !values.birthday}
                                                    inputVariant="outlined"
                                                    fullWidth
                                                    label="Your birthday"
                                                    format="MMM DD, YYYY"
                                                    orientation="landscape"
                                                    openTo="date"
                                                    value={values.birthday || null}
                                                    onChange={(value) => {
                                                        setFieldValue("birthday", moment(value).format("YYYY-MM-DD"));
                                                    }}
                                                    onBlur={handleBlur("birthday")}
                                                />
                                                <ErrorMessage name="birthday">
                                                    {msg => <Typography className={classes.formError}>{msg}</Typography>}
                                                </ErrorMessage>
                                            </Grid>
                                        </Grid>
                                        <Address
                                            isReadyToFetchData={initialDataFetched}
                                            values={values}
                                            setFormikValues={setFormikValues}
                                        />
                                        {!isWhiteLabel &&
                                        <Grid item container alignItems="center" className={classes.formItem}>
                                            <Grid item xs={12} sm={6}>
                                                <Box marginBottom="10px">
                                                    <Typography variant="h5" align={textAlignmentAdaptive}>How did you
                                                        find us?</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field
                                                    component={TextField}
                                                    type="text"
                                                    name="findUs"
                                                    id="findUs"
                                                    aria-labelledby="findUs"
                                                    variant="outlined"
                                                    fullWidth
                                                    label="How did you find us?"
                                                    onChange={handleChange("findUs")}
                                                    onBlur={handleBlur("findUs")}
                                                    value={values.findUs}
                                                    select={true}
                                                    disabled={referralCodeApplied}
                                                    SelectProps={{
                                                        multiple: false,
                                                        SelectDisplayProps: {
                                                            style: {
                                                                width: "100%"
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {
                                                        foundUsOptions.map(option => {
                                                            return <MenuItem key={option.value}
                                                                value={option.value}>{option.label}</MenuItem>;
                                                        })
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        }
                                        {
                                            isOnBoarding && ["friend", "mailer"].includes(values.findUs) &&
                                            <Grid container justify="flex-end" alignItems="center"
                                                className={classes.formItem}>
                                                <Grid item xs={12} sm={6} container alignItems="center"
                                                    justify="space-between">
                                                    <Grid item xs={8}>
                                                        <MUITextField
                                                            fullWidth
                                                            variant="outlined"
                                                            placeholder="Add Referral Code"
                                                            inputProps={{className: classes.input}}
                                                            value={referralCode}
                                                            disabled={referralCodeApplied}
                                                            onChange={(event) => setReferralCode(event.target.value.toUpperCase())}
                                                        />
                                                    </Grid>
                                                    <Grid item xs="auto" justify="flex-end">
                                                        <Button
                                                            type="button"
                                                            variant="contained"
                                                            color="primary"
                                                            className={classes.applyBtn}
                                                            disabled={applyButtonDisabled || referralCodeApplied}
                                                            onClick={applyReferralCode.bind(null, referralCode)}
                                                        >Apply</Button>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        }
                                        <Grid item xs={12} container alignItems="center" className={classes.formItem}>
                                            <Grid item xs={12}>
                                                <Box marginBottom="10px" marginTop="40px">
                                                    <Typography variant="h4" style={{fontSize: 18}}
                                                        align={textAlignmentAdaptive}>
                                                        Do you give {brandingName} access to your location?
                                                    </Typography>
                                                    <Typography align={textAlignmentAdaptive}>
                                                        {brandingName} uses your location to identify which appointment
                                                        types are legally allowed and pair you with a vet in your state
                                                        when necessary.
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <RadioGroup name="location" value={accessData.location.toString()} row
                                                    onChange={(e, val) => handleLocationAccess(e, val)}>
                                                    <FormControlLabel value="true" control={<Radio/>}
                                                        label="I give access to my location"/>
                                                    <FormControlLabel value="false" control={<Radio/>}
                                                        label="I do not give access to my location"/>
                                                </RadioGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container alignItems="center" className={classes.formItem}>
                                            <Grid item xs={12}>
                                                <Box marginBottom="10px" marginTop="40px">
                                                    <Typography variant="h4" style={{fontSize: 18}}
                                                        align={textAlignmentAdaptive}>
                                                        Do you give {brandingName} access to your camera?
                                                    </Typography>
                                                    <Typography align={textAlignmentAdaptive}>
                                                        {brandingName} uses your camera for video appointments.
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <RadioGroup name="camera" value={accessData.camera.toString()} row
                                                    onChange={(e, val) => handleLocationAccess(e, val)}>
                                                    <FormControlLabel value="true" control={<Radio/>}
                                                        label="I give access to my camera"/>
                                                    <FormControlLabel value="false" control={<Radio/>}
                                                        label="I do not give access to my camera"/>
                                                </RadioGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container alignItems="center" className={classes.formItem}>
                                            <Grid item xs={12}>
                                                <Box marginBottom="10px" marginTop="40px">
                                                    <Typography variant="h4" style={{fontSize: 18}}
                                                        align={textAlignmentAdaptive}>
                                                        Do you give {brandingName} access to your microphone?
                                                    </Typography>
                                                    <Typography align={textAlignmentAdaptive}>
                                                        {brandingName} uses your microphone for video appointments.
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <RadioGroup name="microphone" value={accessData.microphone.toString()} row
                                                    onChange={(e, val) => handleLocationAccess(e, val)}>
                                                    <FormControlLabel value="true" control={<Radio/>}
                                                        label="I give access to my microphone"/>
                                                    <FormControlLabel value="false" control={<Radio/>}
                                                        label="I do not give access to my microphone"/>
                                                </RadioGroup>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container justify="flex-end">
                                                <Button variant="contained" fullWidth={isMobile} color="primary"
                                                    type="submit" onClick={()=> setSubmit(true)}>Save</Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    :
                                    <Box height="400px" width="100%" display="flex" justifyContent="center"
                                        alignItems="center">
                                        <CircularProgress/>
                                    </Box>
                            }
                        </Card>
                    </Form>
                );
            }}
        </Formik>

    );
};

export default Basic;
